/**
 * Horizontal placement for an overlay anchored to a trigger.
 * Prefers opening to the right (same idea as trend tooltip placement).
 * Flips left when the right side overflows more. Clamps so the overlay
 * stays inside the viewport when both sides overflow.
 */
export function computeAnchoredOverlayOffset({
  triggerRect,
  overlayWidth,
  viewportWidth,
  padding = 8,
} = {}) {
  const triggerLeft = triggerRect?.left ?? 0;
  const triggerRight = triggerRect?.right ?? triggerLeft;
  const viewWidth = viewportWidth ?? overlayWidth ?? 0;
  const available = Math.max(0, viewWidth - padding * 2);
  const width = Math.min(overlayWidth ?? 0, available);

  const rightLeft = triggerLeft;
  const leftLeft = triggerRight - width;
  const rightClipped = rightLeft + width - (viewWidth - padding);
  const leftClipped = padding - leftLeft;

  const horizontal =
    rightClipped > 0 && rightClipped > leftClipped ? 'left' : 'right';
  const preferred = horizontal === 'right' ? rightLeft : leftLeft;
  const minLeft = padding;
  const maxLeft = viewWidth - padding - width;
  const viewportLeft = Math.min(Math.max(preferred, minLeft), maxLeft);

  return {
    horizontal,
    left: viewportLeft - triggerLeft,
    maxWidth: width,
  };
}

/**
 * Vertical placement for an overlay anchored to a trigger. Prefers opening
 * below; opens above when the overlay doesn't fit below and there's more room
 * above. `gap` is the space between the trigger and the overlay.
 */
export function computeAnchoredOverlayVertical({
  triggerRect,
  overlayHeight,
  viewportHeight,
  gap = 0,
  padding = 8,
} = {}) {
  const needed = (overlayHeight ?? 0) + gap + padding;
  const spaceBelow = (viewportHeight ?? 0) - (triggerRect?.bottom ?? 0);
  const spaceAbove = triggerRect?.top ?? 0;

  return spaceBelow < needed && spaceAbove > spaceBelow ? 'above' : 'below';
}
